import { jsonResponse } from "../http-helpers.js";
/**
 * Settings route module — Effect HttpRouter.Layer (PAN-428 B15)
 *
 * Implements all /api/settings/* endpoints from the Express server:
 *   GET  /api/settings
 *   GET  /api/settings/available-models
 *   GET  /api/settings/optimal-defaults
 *   POST /api/settings/test-api-key
 *   POST /api/settings/validate-api-key
 *   PUT  /api/settings
 */

import { randomBytes } from 'node:crypto';
import { join } from 'node:path';

import { Effect, Layer } from 'effect';
import { HttpRouter, HttpServerRequest } from 'effect/unstable/http';

import {
  loadSettingsApi,
  saveSettingsApi,
  validateSettingsApi,
  getAvailableModelsApi,
  getOptimalDefaultsApi,
  getMiniMaxDefaultsApi,
  saveOpenRouterFavorites,
  getOpenRouterFavorites,
  updateProviderApiKey,
} from '../../../lib/settings-api.js';
import { getClaudeAuthStatus } from '../../../lib/claude-auth.js';
import { setUiTheme } from '../../../lib/ui-theme.js';
import { getOpenAIAuthStatus } from '../../../lib/openai-auth.js';
import { PROVIDERS, getKimiAnthropicBaseUrl } from '../../../lib/providers.js';
import { OpenRouterService } from '../services/openrouter-service.js';
import { httpHandler } from './http-handler.js';
import { getProviderAuthMode, getProviderEnvForModel } from '../../../lib/agents.js';
import { buildHarnessPolicyDecisions } from '../../../lib/harness-policy-decisions.js';
import {
  detectProviderEnvConflicts,
} from '../../../lib/claude-settings-overlay.js';
import { refreshTtsRuntimeConfig } from '../services/tts-runtime-config.js';
import { syncTtsPlaybackWithConfig } from '../services/tts-playback.js';
import { stopConversationSearchWatcher, syncConversationSearchWatcher } from '../services/conversation-search-watcher.js';
import { rejectUnauthorizedDashboardRequest, rejectUnsafeDashboardMutationRequest } from './dashboard-auth.js';
import { validateOrigin } from './origin-validation.js';
import { getConversationSearchConfigSync } from '../../../lib/config-yaml.js';
import { dimensionsForModel, openEmbeddingsDb } from '../../../lib/overdeck/conversations-search.js';
import { createConversationEmbeddingProvider } from '../../../lib/conversation-search/embedding-provider.js';
import { estimateFullReindexConversationSearchCost, fullReindexConversationSearch } from '../../../lib/conversation-search/indexer.js';
import { getLegacyHome } from '../../../lib/paths.js';
import { previewLegacyConversations, importLegacyConversations } from '../../../lib/overdeck/legacy-import.js';

// ─── Local helpers ────────────────────────────────────────────────────────────

const REINDEX_CONFIRM_THRESHOLD_USD = 1;
const REINDEX_CONFIRM_TTL_MS = 10 * 60 * 1000;
const reindexConfirmationNonces = new Map<string, { estimatedUsd: number; expiresAt: number }>();
let activeConversationSearchReindex: Promise<unknown> | null = null;

interface ConversationReindexProgress {
  active: boolean;
  filesScanned: number;
  filesIndexed: number;
  chunksIndexed: number;
  currentFile?: string;
  startedAt?: number;
}
let conversationReindexProgress: ConversationReindexProgress = {
  active: false,
  filesScanned: 0,
  filesIndexed: 0,
  chunksIndexed: 0,
};

function createReindexConfirmationNonce(estimatedUsd: number): string {
  const nonce = randomBytes(24).toString('base64url');
  const now = Date.now();
  for (const [key, entry] of reindexConfirmationNonces) {
    if (entry.expiresAt <= now) reindexConfirmationNonces.delete(key);
  }
  reindexConfirmationNonces.set(nonce, { estimatedUsd, expiresAt: now + REINDEX_CONFIRM_TTL_MS });
  return nonce;
}

function consumeReindexConfirmationNonce(nonce: unknown, estimatedUsd: number): boolean {
  if (typeof nonce !== 'string') return false;
  const entry = reindexConfirmationNonces.get(nonce);
  reindexConfirmationNonces.delete(nonce);
  return !!entry && entry.expiresAt > Date.now() && Math.abs(entry.estimatedUsd - estimatedUsd) < 0.000001;
}

// Read the request body as unknown JSON
const readJsonBody = Effect.gen(function* () {
  const request = yield* HttpServerRequest.HttpServerRequest;
  const text = yield* request.text;
  try {
    return text ? (JSON.parse(text) as unknown) : {};
  } catch {
    return {};
  }
});

/** Model ID to API model ID mapping */
export const MODEL_API_IDS: Record<string, { apiModel: string; endpoint?: string }> = {
  // OpenAI models — gpt-5.x are real OpenAI model IDs (identity map).
  // Codex sign-in routes through CLIProxy; API key routes direct.
  'gpt-5.6-sol': { apiModel: 'gpt-5.6-sol' },
  'gpt-5.6-terra': { apiModel: 'gpt-5.6-terra' },
  'gpt-5.6-luna': { apiModel: 'gpt-5.6-luna' },
  'gpt-5.5-pro': { apiModel: 'gpt-5.5-pro' },
  'gpt-5.5': { apiModel: 'gpt-5.5' },
  'gpt-5.4-pro': { apiModel: 'gpt-5.4-pro' },
  'gpt-5.4': { apiModel: 'gpt-5.4' },
  'gpt-5.4-mini': { apiModel: 'gpt-5.4-mini' },
  'gpt-5.3-codex': { apiModel: 'gpt-5.3-codex' },
  'gpt-5.2': { apiModel: 'gpt-5.2' },
  'o3': { apiModel: 'o3' },
  'o4-mini': { apiModel: 'o4-mini' },
  'o3-deep-research': { apiModel: 'gpt-4o' },
  'gpt-4o': { apiModel: 'gpt-4o' },
  'gpt-4o-mini': { apiModel: 'gpt-4o-mini' },
  'o1': { apiModel: 'gpt-4o' },
  'o3-mini': { apiModel: 'gpt-4o-mini' },
  // Google models
  'gemini-3-pro-preview': { apiModel: 'gemini-1.5-pro' },
  'gemini-3-flash-preview': { apiModel: 'gemini-1.5-flash' },
  'gemini-2.5-pro': { apiModel: 'gemini-1.5-pro' },
  'gemini-2.5-flash': { apiModel: 'gemini-1.5-flash' },
  // Kimi models
  'kimi-k2': { apiModel: 'moonshot-v1-8k' },
  'kimi-k2.5': { apiModel: 'moonshot-v1-32k' },
  'kimi-k2-turbo': { apiModel: 'moonshot-v1-8k' },
  'K2.6-code-preview': { apiModel: 'K2.6-code-preview' },
  // MiniMax models
  'minimax-m2.7': { apiModel: 'minimax-m2.7' },
  'minimax-m2.7-highspeed': { apiModel: 'minimax-m2.7-highspeed' },
  'MiniMax-M3': { apiModel: 'MiniMax-M3' },
  // Z.AI models
  'glm-5.2': { apiModel: 'glm-5.2' },
  'glm-5.1': { apiModel: 'glm-5.1' },
  'glm-4.7': { apiModel: 'glm-4.7' },
  'glm-4.7-flash': { apiModel: 'glm-4.7-flash' },
  // MiMo models
  'mimo-v2.5-pro': { apiModel: 'mimo-v2.5-pro' },
  'mimo-v2.5': { apiModel: 'mimo-v2.5' },
  // Nous Portal models
  'qwen/qwen3.6-plus': { apiModel: 'qwen/qwen3.6-plus' },
  // Alibaba DashScope models
  'qwen3-max': { apiModel: 'qwen3-max' },
  'qwen3-coder-plus': { apiModel: 'qwen3-coder-plus' },
  'qwen3-plus': { apiModel: 'qwen3-plus' },
  'qwen3.7-max': { apiModel: 'qwen3.7-max' },
};

// ─── Route: GET /api/settings ─────────────────────────────────────────────────

const getSettingsRoute = HttpRouter.add(
  'GET',
  '/api/settings',
  httpHandler(Effect.try({
    try: () => jsonResponse(loadSettingsApi()),
    catch: (err) => new Error(err instanceof Error ? err.message : String(err)),
  })),
);

// ─── Route: GET /api/settings/available-models ────────────────────────────────

const getAvailableModelsRoute = HttpRouter.add(
  'GET',
  '/api/settings/available-models',
  httpHandler(Effect.try({
    try: () => jsonResponse(getAvailableModelsApi()),
    catch: (err) => new Error(err instanceof Error ? err.message : String(err)),
  })),
);

// ─── Route: GET /api/settings/optimal-defaults ───────────────────────────────

const getOptimalDefaultsRoute = HttpRouter.add(
  'GET',
  '/api/settings/optimal-defaults',
  httpHandler(Effect.try({
    try: () => jsonResponse(getOptimalDefaultsApi()),
    catch: (err) => new Error(err instanceof Error ? err.message : String(err)),
  })),
);

// ─── Route: GET /api/settings/minimax-defaults ───────────────────────────────

const getMiniMaxDefaultsRoute = HttpRouter.add(
  'GET',
  '/api/settings/minimax-defaults',
  httpHandler(Effect.try({
    try: () => jsonResponse(getMiniMaxDefaultsApi()),
    catch: (err) => new Error(err instanceof Error ? err.message : String(err)),
  })),
);

// ─── Route: GET /api/settings/claude-auth ────────────────────────────────────

const getClaudeAuthRoute = HttpRouter.add(
  'GET',
  '/api/settings/claude-auth',
  httpHandler(Effect.gen(function* () {
    const status = yield* getClaudeAuthStatus();
    return jsonResponse(status);
  })),
);

// ─── Route: GET /api/settings/openai-auth ────────────────────────────────────

const getOpenAIAuthRoute = HttpRouter.add(
  'GET',
  '/api/settings/openai-auth',
  httpHandler(Effect.gen(function* () {
    const status = yield* getOpenAIAuthStatus();
    return jsonResponse(status);
  })),
);

// ─── Route: POST /api/settings/test-api-key ──────────────────────────────────

const postTestApiKeyRoute = HttpRouter.add(
  'POST',
  '/api/settings/test-api-key',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;
    const { provider, apiKey, model } = body as Record<string, string | undefined>;

    if (!provider || !apiKey) {
      return jsonResponse({ error: 'Provider and apiKey are required' }, { status: 400 });
    }

    // Test the API key by sending a minimal prompt. Errors are returned as
    // response data (success: false, error: '...') rather than HTTP errors,
    // since partial failures (wrong model, rate limits) are expected and useful.
    return yield* Effect.promise(async () => {
      let success = false;
      let error: string | null = null;
      let response: string | null = null;
      let latencyMs = 0;
      const testPrompt = 'What is 2+3? Reply with just the number.';
      const expectedAnswer = '5';
      const startTime = Date.now();

      switch (provider) {
        case 'openai': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'gpt-4o-mini') : 'gpt-4o-mini';
          try {
            const resp = await fetch('https://api.openai.com/v1/chat/completions', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            if (resp.ok) {
              const data = await resp.json() as { choices?: Array<{ message?: { content?: string } }> };
              response = data.choices?.[0]?.message?.content?.trim() || '';
              success = response.includes(expectedAnswer);
              if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${(await resp.text()).slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'google': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'gemini-1.5-flash') : 'gemini-1.5-flash';
          try {
            const resp = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${apiModel}:generateContent?key=${apiKey}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contents: [{ parts: [{ text: testPrompt }] }], generationConfig: { maxOutputTokens: 10 } }),
            });
            latencyMs = Date.now() - startTime;
            if (resp.ok) {
              const data = await resp.json() as { candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }> };
              response = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || '';
              success = response.includes(expectedAnswer);
              if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
            } else if (resp.status === 400 || resp.status === 403) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${(await resp.text()).slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'kimi': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'K2.6-code-preview') : 'K2.6-code-preview';
          try {
            const resp = await fetch(`${getKimiAnthropicBaseUrl(apiKey)}v1/messages`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            const responseText = await resp.text();
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { content?: Array<{ text?: string }> };
                response = data.content?.[0]?.text?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch {
                error = `Kimi returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'minimax': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'minimax-m2.7') : 'minimax-m2.7';
          try {
            const resp = await fetch('https://api.minimax.io/anthropic/v1/messages', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            const contentType = resp.headers.get('content-type');
            const location = resp.headers.get('location');
            const responseText = await resp.text();
            console.error('[settings:test-api-key:minimax]', JSON.stringify({
              status: resp.status,
              redirected: resp.redirected,
              url: resp.url,
              location,
              contentType,
              bodyPreview: responseText.slice(0, 300),
            }));
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { content?: Array<{ text?: string }>; usage?: unknown };
                console.error('[settings:test-api-key:minimax:parsed]', JSON.stringify({
                  hasContent: Array.isArray(data.content),
                  usageType: typeof data.usage,
                  keys: Object.keys(data),
                }));
                response = data.content?.[0]?.text?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch (parseErr) {
                console.error('[settings:test-api-key:minimax:parse-error]', parseErr);
                error = `MiniMax returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            console.error('[settings:test-api-key:minimax:request-error]', err);
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'zai': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'glm-5.2') : 'glm-5.2';
          try {
            const resp = await fetch('https://api.z.ai/api/anthropic/v1/messages', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            const responseText = await resp.text();
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { content?: Array<{ text?: string }> };
                response = data.content?.[0]?.text?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch {
                error = `Z.AI returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'mimo': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'mimo-v2.5-pro') : 'mimo-v2.5-pro';
          try {
            const resp = await fetch('https://token-plan-sgp.xiaomimimo.com/anthropic/v1/messages', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 128 }),
            });
            latencyMs = Date.now() - startTime;
            const responseText = await resp.text();
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { content?: Array<{ type?: string; text?: string }> };
                const textBlock = data.content?.find(b => b.type === 'text');
                response = textBlock?.text?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch {
                error = `MiMo returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'nous': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'qwen/qwen3.6-plus') : 'qwen/qwen3.6-plus';
          try {
            const resp = await fetch('https://inference-api.nousresearch.com/v1/chat/completions', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            const responseText = await resp.text();
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { choices?: Array<{ message?: { content?: string | null } }> };
                response = data.choices?.[0]?.message?.content?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch {
                error = `Nous Portal returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'dashscope': {
          const apiModel = model ? (MODEL_API_IDS[model]?.apiModel || 'qwen3-max') : 'qwen3-max';
          try {
            const resp = await fetch('https://dashscope-intl.aliyuncs.com/compatible-mode/v1/chat/completions', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: apiModel, messages: [{ role: 'user', content: testPrompt }], max_tokens: 10 }),
            });
            latencyMs = Date.now() - startTime;
            const responseText = await resp.text();
            if (resp.ok) {
              try {
                const data = JSON.parse(responseText) as { choices?: Array<{ message?: { content?: string | null } }> };
                response = data.choices?.[0]?.message?.content?.trim() || '';
                success = response.includes(expectedAnswer);
                if (!success) error = `Model returned: ${response} (expected ${expectedAnswer})`;
              } catch {
                error = `DashScope returned non-JSON response: ${responseText.slice(0, 100)}`;
              }
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 404) {
              error = `Model not found: ${apiModel}`;
            } else {
              error = `HTTP ${resp.status}: ${responseText.slice(0, 100)}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        default:
          error = `Unknown provider: ${provider}`;
      }

      return jsonResponse({ success, error, response, latencyMs, model: model || 'default' });
    });
  })),
);

// ─── Route: POST /api/settings/validate-api-key ──────────────────────────────

const postValidateApiKeyRoute = HttpRouter.add(
  'POST',
  '/api/settings/validate-api-key',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;
    const { provider, apiKey } = body as Record<string, string | undefined>;

    if (!provider || !apiKey) {
      return jsonResponse({ error: 'Provider and apiKey are required' }, { status: 400 });
    }

    if (!['openai', 'google', 'kimi', 'minimax', 'zai', 'mimo', 'nous', 'dashscope'].includes(provider)) {
      return jsonResponse({ error: `Unsupported provider: ${provider}` }, { status: 400 });
    }

    // Validate by listing models or sending a probe request. Errors returned as data.
    return yield* Effect.promise(async () => {
      let valid = false;
      let error: string | null = null;
      let models: string[] = [];

      switch (provider) {
        case 'openai': {
          try {
            const resp = await fetch('https://api.openai.com/v1/models', {
              headers: { 'Authorization': `Bearer ${apiKey}` },
            });
            if (resp.ok) {
              const data = await resp.json() as { data: Array<{ id: string }> };
              valid = true;
              models = data.data
                .map(m => m.id)
                .filter(id => id.includes('gpt-') || id.includes('o1') || id.includes('o3'));
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'google': {
          try {
            const endpoint = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview' + ':generateContent';
            const resp = await fetch(`${endpoint}?key=${apiKey}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contents: [{ parts: [{ text: 'test' }] }] }),
            });
            if (resp.ok || resp.status === 400) {
              valid = true;
              models = ['gemini-3-pro-preview', 'gemini-3-flash-preview'];
            } else if (resp.status === 401 || resp.status === 403) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'kimi': {
          try {
            const resp = await fetch(`${getKimiAnthropicBaseUrl(apiKey)}v1/models`, {
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01' },
            });
            if (resp.ok) {
              const data = await resp.json() as { data?: Array<{ id: string }> };
              valid = true;
              models = data.data?.map(m => m.id) || ['kimi-k2.5', 'K2.6-code-preview'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'minimax': {
          try {
            const resp = await fetch('https://api.minimax.io/anthropic/v1/models', {
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01' },
            });
            if (resp.ok) {
              const data = await resp.json() as { data?: Array<{ id: string }> };
              valid = true;
              models = data.data?.map(m => m.id) || ['minimax-m2.7', 'minimax-m2.7-highspeed'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'zai': {
          try {
            const resp = await fetch('https://api.z.ai/api/anthropic/v1/models', {
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01' },
            });
            if (resp.ok) {
              const data = await resp.json() as { data?: Array<{ id: string }> };
              valid = true;
              models = data.data?.map(m => m.id) || ['glm-5.2'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'mimo': {
          // MiMo subscription endpoint does not expose /v1/models; validate via a lightweight messages request
          try {
            const resp = await fetch('https://token-plan-sgp.xiaomimimo.com/anthropic/v1/messages', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${apiKey}`, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' },
              body: JSON.stringify({ model: 'mimo-v2.5-pro', messages: [{ role: 'user', content: 'Hi' }], max_tokens: 1 }),
            });
            if (resp.ok) {
              valid = true;
              models = ['mimo-v2.5-pro', 'mimo-v2.5'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'nous': {
          try {
            const resp = await fetch('https://inference-api.nousresearch.com/v1/models', {
              headers: { 'Authorization': `Bearer ${apiKey}` },
            });
            if (resp.ok) {
              const data = await resp.json() as { data?: Array<{ id: string }> };
              valid = true;
              models = data.data?.map(m => m.id) || ['qwen/qwen3.6-plus'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }

        case 'dashscope': {
          try {
            const resp = await fetch('https://dashscope-intl.aliyuncs.com/compatible-mode/v1/models', {
              headers: { 'Authorization': `Bearer ${apiKey}` },
            });
            if (resp.ok) {
              const data = await resp.json() as { data?: Array<{ id: string }> };
              valid = true;
              models = data.data?.map(m => m.id) || ['qwen3-max', 'qwen3-coder-plus', 'qwen3-plus', 'qwen3.7-max'];
            } else if (resp.status === 401) {
              error = 'Invalid API key';
            } else if (resp.status === 429) {
              error = 'Rate limit exceeded';
            } else {
              error = `HTTP error: ${resp.status}`;
            }
          } catch (err) {
            error = `Network error: ${err instanceof Error ? err.message : String(err)}`;
          }
          break;
        }
      }

      return jsonResponse({ valid, provider, models: valid ? models : undefined, error: error || undefined });
    });
  })),
);

// ─── Routes: Conversation search status / reindex ─────────────────────────────

const getConversationSearchStatusRoute = HttpRouter.add(
  'GET',
  '/api/settings/conversation-search/status',
  httpHandler(Effect.gen(function* () {
    return yield* Effect.try({
      try: () => {
        const config = getConversationSearchConfigSync();
        if (!config.enabled) {
          return jsonResponse({
            enabled: false,
            available: false,
            unavailableReason: 'conversationSearch is disabled',
            dbPath: config.dbPath,
            chunkCount: 0,
            indexedFileCount: 0,
            lastIndexedAt: null,
          });
        }

        const provider = createConversationEmbeddingProvider({ config });
        if (!provider.enabled) {
          return jsonResponse({
            enabled: config.enabled,
            available: false,
            unavailableReason: provider.unavailableReason ?? 'embedding provider unavailable',
            dbPath: config.dbPath,
            chunkCount: 0,
            indexedFileCount: 0,
            lastIndexedAt: null,
          });
        }

        const db = openEmbeddingsDb(config.dbPath, dimensionsForModel(config.model));
        try {
          const stats = db.getStats();
          return jsonResponse({
            enabled: config.enabled,
            available: db.available && provider.enabled,
            unavailableReason: db.unavailableReason,
            dbPath: config.dbPath,
            ...stats,
          });
        } finally {
          db.close();
        }
      },
      catch: (err) => new Error(err instanceof Error ? err.message : String(err)),
    });
  })),
);

const getConversationSearchReindexEstimateRoute = HttpRouter.add(
  'GET',
  '/api/settings/conversation-search/reindex-estimate',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    return yield* Effect.try({
      try: async () => {
        // Optional ?model= lets the UI price a *prospective* model switch before saving it.
        const modelOverride = new URL(request.url, 'http://localhost').searchParams.get('model')?.trim();
        const baseConfig = getConversationSearchConfigSync();
        const config = modelOverride ? { ...baseConfig, model: modelOverride } : baseConfig;
        const estimate = await estimateFullReindexConversationSearchCost({ config });
        const confirmationNonce = estimate.estimatedUsd > REINDEX_CONFIRM_THRESHOLD_USD
          ? createReindexConfirmationNonce(estimate.estimatedUsd)
          : undefined;
        return jsonResponse({ ...estimate, confirmationNonce });
      },
      catch: (err) => {
        const message = err instanceof Error ? `${err.message}\n${err.stack}` : String(err);
        console.error(`[conversation-search] reindex estimate failed: ${message}`);
        return jsonResponse({ error: err instanceof Error ? err.message : String(err) }, { status: 500 });
      },
    });
  })),
);

const postConversationSearchReindexRoute = HttpRouter.add(
  'POST',
  '/api/settings/conversation-search/reindex',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    const authError = rejectUnsafeDashboardMutationRequest(request);
    if (authError) return authError;
    const body = yield* readJsonBody;

    return yield* Effect.promise(async () => {
      if (activeConversationSearchReindex) {
        return jsonResponse({ error: 'Conversation search reindex already in progress' }, { status: 409 });
      }

      const job = (async () => {
        const estimate = await estimateFullReindexConversationSearchCost();
        if (estimate.disabled) return jsonResponse(estimate);
        if (estimate.estimatedUsd > REINDEX_CONFIRM_THRESHOLD_USD) {
          const nonce = (body as { confirmationNonce?: unknown }).confirmationNonce;
          if (!consumeReindexConfirmationNonce(nonce, estimate.estimatedUsd)) {
            return jsonResponse({
              error: 'Reindex confirmation required',
              requiresConfirmation: true,
              estimate,
            }, { status: 409 });
          }
        }

        await stopConversationSearchWatcher();
        conversationReindexProgress = { active: true, filesScanned: 0, filesIndexed: 0, chunksIndexed: 0, startedAt: Date.now() };
        try {
          const result = await fullReindexConversationSearch({
            onProgress: (p) => {
              conversationReindexProgress = { active: true, startedAt: conversationReindexProgress.startedAt, ...p };
            },
          });
          return jsonResponse(result);
        } finally {
          conversationReindexProgress = { ...conversationReindexProgress, active: false };
          await syncConversationSearchWatcher();
        }
      })();
      activeConversationSearchReindex = job;
      try {
        return await job;
      } finally {
        if (activeConversationSearchReindex === job) activeConversationSearchReindex = null;
      }
    });
  })),
);

// Read-only progress for the live reindex bar. Public (same rationale as the status route).
const getConversationSearchReindexProgressRoute = HttpRouter.add(
  'GET',
  '/api/settings/conversation-search/reindex-progress',
  httpHandler(Effect.gen(function* () {
    return jsonResponse(conversationReindexProgress);
  })),
);

// ─── Route: PUT /api/settings ─────────────────────────────────────────────────

const putSettingsRoute = HttpRouter.add(
  'PUT',
  '/api/settings',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;

    return yield* Effect.promise(async () => {
      try {
        const newSettings = body as Parameters<typeof validateSettingsApi>[0];
        const validation = validateSettingsApi(newSettings);
        if (!validation.valid) {
          return jsonResponse({ error: validation.errors.join('; ') }, { status: 400 });
        }
        await Effect.runPromise(saveSettingsApi(newSettings));
        await refreshTtsRuntimeConfig();
        await syncTtsPlaybackWithConfig();
        await syncConversationSearchWatcher();
        return jsonResponse({
          success: true,
          message: 'Settings saved to config.yaml',
          warnings: validation.warnings.length > 0 ? validation.warnings : undefined,
        });
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : String(err));
      }
    });
  })),
);

// ─── Route: PUT /api/settings/ui-theme ────────────────────────────────────────
// The frontend syncs its resolved theme here on load and on toggle. New tmux
// sessions stamp their pane background from this value so Claude Code's
// `theme: auto` detects the dashboard theme even when started headless.

const putUiThemeRoute = HttpRouter.add(
  'PUT',
  '/api/settings/ui-theme',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;

    return yield* Effect.promise(async () => {
      const theme = (body as { theme?: unknown } | null)?.theme;
      if (theme !== 'light' && theme !== 'dark') {
        return jsonResponse({ error: "theme must be 'light' or 'dark'" }, { status: 400 });
      }
      await setUiTheme(theme);
      return jsonResponse({ success: true });
    });
  })),
);

// ─── Route: GET /api/settings/openrouter/models ──────────────────────────────

const getOpenRouterModelsRoute = HttpRouter.add(
  'GET',
  '/api/settings/openrouter/models',
  httpHandler(Effect.gen(function* () {
    const orService = yield* OpenRouterService;
    const models = yield* orService.fetchModels();
    const favorites = getOpenRouterFavorites();
    return jsonResponse({ models, favorites });
  })),
);

// ─── Route: PUT /api/settings/openrouter/favorites ───────────────────────────

const putOpenRouterFavoritesRoute = HttpRouter.add(
  'PUT',
  '/api/settings/openrouter/favorites',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;
    const { favorites } = body as { favorites?: unknown };

    if (!Array.isArray(favorites)) {
      return jsonResponse({ error: 'favorites must be an array of model IDs' }, { status: 400 });
    }

    const modelIds = favorites.filter((f): f is string => typeof f === 'string');
    return yield* Effect.promise(async () => {
      try {
        await Effect.runPromise(saveOpenRouterFavorites(modelIds));
        return jsonResponse({ success: true, favorites: modelIds });
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : String(err));
      }
    });
  })),
);

// ─── Route: PUT /api/settings/openrouter/api-key ─────────────────────────────

const putOpenRouterApiKeyRoute = HttpRouter.add(
  'PUT',
  '/api/settings/openrouter/api-key',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;
    const { apiKey } = body as { apiKey?: unknown };

    if (apiKey !== undefined && typeof apiKey !== 'string') {
      return jsonResponse({ error: 'apiKey must be a string when provided' }, { status: 400 });
    }

    return yield* Effect.promise(async () => {
      try {
        const settings = await Effect.runPromise(updateProviderApiKey('openrouter', apiKey?.trim() || undefined));
        return jsonResponse({
          success: true,
          apiKey: settings.api_keys.openrouter,
          message: 'OpenRouter API key saved',
        });
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : String(err));
      }
    });
  })),
);

// ─── Route: POST /api/settings/openrouter/test-key ───────────────────────────

const postOpenRouterTestKeyRoute = HttpRouter.add(
  'POST',
  '/api/settings/openrouter/test-key',
  httpHandler(Effect.gen(function* () {
    const body = yield* readJsonBody;
    const { apiKey } = body as { apiKey?: string };

    if (!apiKey) {
      return jsonResponse({ error: 'apiKey is required' }, { status: 400 });
    }

    const orService = yield* OpenRouterService;
    const result = yield* orService.validateApiKey(apiKey);
    return jsonResponse(result);
  })),
);

// ─── Route: GET /api/settings/harness-policy ────────────────────────────────

const SAFE_MODEL_PATTERN = /^[a-zA-Z0-9_.:\/-]+$/;
const MAX_HARNESS_POLICY_MODELS = 250;
const MAX_HARNESS_POLICY_MODEL_LENGTH = 200;

const getHarnessPolicyRoute = HttpRouter.add(
  'GET',
  '/api/settings/harness-policy',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    return yield* Effect.promise(async () => {
      const url = new URL(request.url, 'http://localhost');
      const models = (url.searchParams.get('models') ?? '')
        .split(',')
        .map((model) => model.trim())
        .filter(Boolean);

      if (
        models.length === 0
        || models.length > MAX_HARNESS_POLICY_MODELS
        || models.some((model) => model.length > MAX_HARNESS_POLICY_MODEL_LENGTH || !SAFE_MODEL_PATTERN.test(model))
      ) {
        return jsonResponse({ error: 'Valid models parameter is required' }, { status: 400 });
      }

      // The per-model loop is factored into a pure helper
      // (src/lib/harness-policy-decisions.ts) so it can be unit-tested
      // without an Effect HTTP server. The helper emits 'ohmypi' decisions
      // — the prior 'pi' key was silently dropping the ToS block that
      // pickers query under 'ohmypi' (PAN-2528).
      const decisions = await buildHarnessPolicyDecisions(models, getProviderAuthMode);
      return jsonResponse({ decisions });
    });
  })),
);

// ─── Route: GET /api/settings/provider-env-conflicts ─────────────────────────


const getProviderEnvConflictsRoute = HttpRouter.add(
  'GET',
  '/api/settings/provider-env-conflicts',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    return yield* Effect.promise(async () => {
      const url = new URL(request.url, 'http://localhost');
      const model = url.searchParams.get('model');
      if (!model || !SAFE_MODEL_PATTERN.test(model)) {
        return jsonResponse({ error: 'Valid model parameter is required' }, { status: 400 });
      }

      try {
        const providerEnv = await getProviderEnvForModel(model);
        const conflicts = await Effect.runPromise(detectProviderEnvConflicts(providerEnv));
        return jsonResponse({ conflicts });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return jsonResponse({ error: msg }, { status: 500 });
      }
    });
  })),
);


const getLegacyImportPreviewRoute = HttpRouter.add(
  'GET',
  '/api/settings/legacy-import/conversations',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    return yield* Effect.try({
      try: () => {
        const urlPath = new URL(request.url, 'http://localhost').searchParams.get('path');
        const defaultPath = join(getLegacyHome(), 'panopticon.db');
        const resolvedPath = urlPath?.trim() || defaultPath;
        const preview = previewLegacyConversations(resolvedPath);
        if (!preview.found) {
          return jsonResponse({ found: false, defaultPath, message: `No legacy database found at ${resolvedPath}` });
        }
        return jsonResponse({ found: true, path: resolvedPath, conversations: preview.rows });
      },
      catch: (err) => jsonResponse({ error: err instanceof Error ? err.message : String(err) }, { status: 500 }),
    });
  })),
);

const postLegacyImportRoute = HttpRouter.add(
  'POST',
  '/api/settings/legacy-import/conversations',
  httpHandler(Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    const authError = rejectUnsafeDashboardMutationRequest(request);
    if (authError) return authError;
    const body = yield* readJsonBody;
    return yield* Effect.try({
      try: () => {
        const { path, names } = body as { path: string; names: string[] };
        const result = importLegacyConversations(path, names);
        return jsonResponse(result);
      },
      catch: (err) => jsonResponse({ error: err instanceof Error ? err.message : String(err) }, { status: 500 }),
    });
  })),
);

// ─── Compose all routes into a single Layer ───────────────────────────────────

export const settingsRouteLayer = Layer.mergeAll(
  getSettingsRoute,
  getAvailableModelsRoute,
  getOptimalDefaultsRoute,
  getMiniMaxDefaultsRoute,
  getClaudeAuthRoute,
  getOpenAIAuthRoute,
  postTestApiKeyRoute,
  postValidateApiKeyRoute,
  getConversationSearchStatusRoute,
  getConversationSearchReindexEstimateRoute,
  postConversationSearchReindexRoute,
  getConversationSearchReindexProgressRoute,
  putSettingsRoute,
  putUiThemeRoute,
  getOpenRouterModelsRoute,
  putOpenRouterFavoritesRoute,
  putOpenRouterApiKeyRoute,
  postOpenRouterTestKeyRoute,
  getHarnessPolicyRoute,
  getProviderEnvConflictsRoute,
  getLegacyImportPreviewRoute,
  postLegacyImportRoute,
);

export default settingsRouteLayer;
